import { createSelector } from '@reduxjs/toolkit'
import moment from 'moment'

import { DINING_METHOD } from '@common'
import { calculateCartTotals, getEligiblePromos } from '@utils'

// Base selectors
const selectCartState = state => state.cart
const selectOrderState = state => state.order
const selectUserState = state => state.user
const selectMembershipState = state => state.membership

// Cart selectors
export const selectCartItems = createSelector([selectCartState], cart => cart.items)

export const selectCreditToUseCents = createSelector([selectCartState], cart => cart.creditToUseCents)

export const selectIsComplimentaryOrder = createSelector([selectCartState], cart => cart.isComplimentaryOrder)

// Order selectors
export const selectAvailablePromos = createSelector([selectOrderState], order => order.availablePromos)

// Timestamp to trigger promo recalculation (updated when cart modal opens)
export const selectPromoCheckTimestamp = createSelector([selectOrderState], order => order.promoCheckTimestamp)

// User selectors
export const selectDiningMethod = createSelector([selectUserState], user => user.diningMethod)

export const selectPickupDate = createSelector([selectUserState], user => user.pickupDate)

export const selectPickupTime = createSelector([selectUserState], user => user.pickupTime)

// Membership selectors
export const selectMemberData = createSelector([selectMembershipState], membership => membership.memberData)

export const selectDiscountPct = createSelector([selectMemberData], memberData => memberData?.benefit?.discountPct || 0)

// Promo reference moment (for advance takeaway, use pickup date/time)
// Depends on promoCheckTimestamp to force recalculation when cart modal opens
// This ensures time-based promos are checked against current time
export const selectPromoReferenceMoment = createSelector(
    [selectDiningMethod, selectPickupDate, selectPickupTime, selectPromoCheckTimestamp],
    (diningMethod, pickupDate, pickupTime, _promoCheckTimestamp) => {
        // _promoCheckTimestamp is used to invalidate cache and force recalculation
        if (diningMethod?.value !== DINING_METHOD.AdvanceTakeaway) {
            return moment()
        }
        if (!pickupDate || !pickupTime) {
            return moment()
        }
        const dateStr = pickupDate.value || pickupDate
        const timeStr = pickupTime.value || pickupTime
        return moment(`${dateStr} ${timeStr}`, 'YYYY-MM-DD HH:mm')
    }
)

// Eligible promos based on cart items and available promos
export const selectEligiblePromos = createSelector(
    [selectCartItems, selectAvailablePromos, selectPromoReferenceMoment],
    (items, availablePromos, promoReferenceMoment) => {
        return getEligiblePromos(items, availablePromos, promoReferenceMoment)
    }
)

// Promo discount amount
export const selectPromoDiscount = createSelector([selectEligiblePromos], eligiblePromos => {
    return eligiblePromos.reduce((sum, promo) => sum + (promo.discountAmount || 0), 0)
})

// Requested promo types (for order submission)
export const selectRequestedPromos = createSelector([selectEligiblePromos], eligiblePromos => {
    return eligiblePromos.map(promo => promo.promoType)
})

// Cart totals with all discounts applied
export const selectCartTotals = createSelector(
    [selectCartItems, selectCreditToUseCents, selectDiscountPct, selectIsComplimentaryOrder, selectPromoDiscount],
    (items, creditToUseCents, discountPct, isComplimentaryOrder, promoDiscount) => {
        return calculateCartTotals(items, creditToUseCents, discountPct, isComplimentaryOrder, promoDiscount)
    }
)
